import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  requestNotificationPermission,
  getFCMToken,
  isNotificationSupported,
} from '../../helpers/firebaseMessaging';
import './NotificationPermissionBanner.scss';

const NotificationPermissionModal = ({ onPermissionGranted, onPermissionDenied, forceShow = false, isDenied = false }) => {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [permissionStatus, setPermissionStatus] = useState('default');

  useEffect(() => {
    // Check if notifications are supported
    if (!isNotificationSupported()) {
      return;
    }

    // Check current permission status
    const permission = Notification.permission;
    setPermissionStatus(permission);

    // Show modal if forced, if permission is default (not yet asked), or if explicitly marked as denied
    if (forceShow || permission === 'default' || isDenied) {
      // Delay showing modal to not annoy user immediately
      const timer = setTimeout(() => {
        setShowModal(true);
      }, forceShow || isDenied ? 0 : 3000); // Show immediately if forced or denied, otherwise after 3 seconds

      return () => clearTimeout(timer);
    }
  }, [forceShow, isDenied]);

  const handleRequestPermission = async () => {
    setLoading(true);
    try {
      const permission = await requestNotificationPermission();
      setPermissionStatus(permission);

      if (permission === 'granted') {
        // Get FCM token after permission is granted
        const token = await getFCMToken();
        if (token && onPermissionGranted) {
          onPermissionGranted(token);
        }
        setShowModal(false);
      } else {
        // Permission was denied - update to show browser settings guidance
        if (onPermissionDenied) {
          onPermissionDenied();
        }
        // Don't close modal if permission is denied, let user see the guidance
        if (permission === 'denied') {
          setLoading(false);
          return;
        }
        setShowModal(false);
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      setShowModal(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setShowModal(false);
    if (onPermissionDenied) {
      onPermissionDenied();
    }
  };

  // Don't show modal if permission is already granted (unless forced or explicitly denied)
  if (!showModal || (!forceShow && !isDenied && permissionStatus !== 'default')) {
    return null;
  }

  // Determine if we should show browser settings guidance (permission denied)
  const showBrowserSettings = isDenied || permissionStatus === 'denied';

  return (
    <div className="notification-permission-modal__overlay" role="dialog" aria-modal="true">
      <div className="notification-permission-modal__card">
        {/* Icon/Glyph */}
        <div className="notification-permission-modal__glyph" aria-hidden="true">
          <div className="notification-permission-modal__glyph-core">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
            </svg>
          </div>
        </div>

        {/* Title */}
        <h2 className="notification-permission-modal__title">
          {showBrowserSettings ? 'Notifications Blocked' : 'Enable Notifications'}
        </h2>

        {/* Message */}
        <p className="notification-permission-modal__message">
          {showBrowserSettings
            ? 'Notifications are currently blocked in your browser settings. To enable notifications, please allow them in your browser settings and try again.'
            : 'Stay updated by allowing notifications from the app.'
          }
        </p>

        {/* Why we need it section */}
        {!showBrowserSettings && (
          <div className="notification-permission-modal__why-section">
            <h3 className="notification-permission-modal__why-title">Why we need it</h3>
            <ul className="notification-permission-modal__why-list">
              <li>Stay updated on Platform</li>
              <li>Receive reward and voucher notifications</li>
              <li>Get important account notifications</li>
            </ul>
          </div>
        )}

        {/* Note section */}
        {!showBrowserSettings && (
          <div className="notification-permission-modal__note">
            <span className="notification-permission-modal__note-label">Note:</span>
            <span className="notification-permission-modal__note-text">
              You can change your notification preferences at any time in your account settings.
            </span>
          </div>
        )}

        {/* Browser Settings Guidance */}
        {showBrowserSettings && (
          <div className="notification-permission-modal__guidance">
            <p className="notification-permission-modal__guidance-text">
              <strong>Important:</strong> Browser permissions were previously blocked.
            </p>
            <p className="notification-permission-modal__guidance-text">
              You must enable notifications in your browser settings first, then return here to complete setup.
            </p>
            <ul className="notification-permission-modal__guidance-list">
              <li>Click the lock/info icon 🔒 in your browser's address bar</li>
              <li>Find "Notifications" in the permissions section</li>
              <li>Change the setting to "Allow"</li>
              <li>Return to this page and click "Try Again"</li>
            </ul>
          </div>
        )}

        {/* Actions */}
        <div className="notification-permission-modal__actions">
          {showBrowserSettings ? (
            <>
              <button
                type="button"
                className="notification-permission-modal__button notification-permission-modal__button--secondary"
                onClick={handleDismiss}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="notification-permission-modal__button notification-permission-modal__button--primary"
                onClick={handleRequestPermission}
                disabled={loading}
              >
                {loading ? 'Checking...' : 'Try Again'}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="notification-permission-modal__button notification-permission-modal__button--secondary"
                onClick={handleDismiss}
                disabled={loading}
              >
                Not Now
              </button>
              <button
                type="button"
                className="notification-permission-modal__button notification-permission-modal__button--primary"
                onClick={handleRequestPermission}
                disabled={loading}
              >
                {loading ? 'Enabling...' : 'Enable'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

NotificationPermissionModal.propTypes = {
  onPermissionGranted: PropTypes.func,
  onPermissionDenied: PropTypes.func,
  forceShow: PropTypes.bool,
  isDenied: PropTypes.bool,
};

export default NotificationPermissionModal;
